import { useEffect } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link } from "react-router-dom";
import { ChevronLeft, LineChart, ArrowUpRight, ShieldCheck, Layers, GitBranch, Bell, Check, Gauge, Rocket } from "lucide-react";
import { useSeo, JsonLd, SITE_URL, PERSON_ID } from "@/lib/seo";
import { IndicatorReviews } from "@/components/landing/IndicatorReviews";

const TV_URL = "https://www.tradingview.com/script/MiXDrieo-TradrQuant-Pro/";
const PAGE_PATH = "/tools/tradrquant-pro-indicator";
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;
const IMAGE = `${SITE_URL}/tq-logo.png`;

const ENGINE = [
  ["Trend Cloud", "A fast/slow EMA ribbon (21/50 default) plus a 200-EMA major trend filter. This is your directional bias layer: the foundation everything else builds on."],
  ["Quant Force Baseline", "An adaptive baseline built on Kaufman's Efficiency Ratio. It accelerates and hugs price during strong directional moves, and flattens during chop — reacting to the character of the move itself rather than a fixed lookback. Wrapped in an ATR envelope, a directional flip only confirms after a configurable run of consecutive closes beyond the band, filtering out single-candle noise before it ever reaches the confluence layer."],
  ["Confluence Score (0–6)", "Six independent structural checks: EMA trend direction, major trend alignment, Quant Force direction, RSI momentum side, volume expansion, and non-overextension against the Reversal Bands. A setup needs a minimum score before it's even eligible."],
  ["Quality Score (0–100)", "A second, fully independent gate: momentum strength, volume ratio versus average, candle body-to-range ratio, and baseline efficiency. This measures how clean the move is, separate from which direction it's in — a dual-gate structure, not a single blended number."],
  ["Multi-Timeframe Confluence", "Cross-checks trend alignment across three configurable higher timeframes (default 1H / 4H / Daily) using the same EMA logic as the primary chart. Can be required for a setup to fire, or simply displayed for read-only context."],
  ["Anti-Cluster Cooldown", "Enforces a minimum bar spacing between setups, so a choppy session can't spam repeated triggers off the same move."],
];

const TRADE = [
  ["Reading the ribbon (Trend Cloud)", "The fast/slow EMA ribbon is your directional filter before anything else. As a baseline rule: favor long entries only while price is trading above the ribbon and the cloud is coloring bullish (fast EMA above slow EMA). Favor short entries only while price is below the ribbon and the cloud is bearish. Trading against the ribbon's current color is counter-trend by definition — treat those setups, if you take them at all, as lower-conviction, tighter-risk plays, not your core approach."],
  ["Reading the Quant Force baseline and envelope", "Price closing beyond the ATR envelope in the direction of a fresh Quant Force flip is your momentum confirmation layer, sitting on top of the ribbon. When the baseline, the envelope flip, and the ribbon direction all agree, that's the strongest form of trend alignment the engine can show you."],
  ["Taking the BUY / SELL setups", "A printed BUY only appears when the confluence score, quality score, and (if enabled) MTF agreement all clear their thresholds simultaneously — it is the culmination of every layer above, not a standalone trigger. The same applies to SELL, mirrored to the downside. Treat every printed setup as a starting point for your own trade construction, not an instruction to execute blindly."],
  ["Using the dashboard as a pre-flight check", "Before acting on any setup, glance at the panel: CONF shows how many of the six confluence checks are currently satisfied, QUAL shows the 0–100 quality reading, GATE shows ARMED or BUILDING, and MTF shows the letter-by-letter breakdown across your three higher timeframes. A setup backed by a high CONF score, a QUAL reading comfortably above your threshold, and full MTF agreement (all letters matching direction) is structurally stronger than one that just barely cleared the minimum bar."],
  ["Using zones as confluence, not as triggers", "Supply/Demand and Support/Resistance zones are context, not entry signals on their own. A BUY setup that prints at or just above a demand zone is meaningfully stronger than the same BUY setup printing in open air with no nearby structure. Use the zones to grade the setups the engine gives you, not to generate new ones independently."],
  ["Using Session VWAP as an intraday bias filter", "On intraday timeframes, treat price trading above the anchored session VWAP as a bullish bias filter and below it as bearish. A BUY setup above VWAP and a SELL setup below VWAP are both trading with the day's volume-weighted flow rather than against it."],
  ["Managing the TP/SL suite", "Every setup draws a full SL, TP1, TP2, and TP3, all derived from live ATR rather than fixed points. A disciplined starting framework: scale a portion of size off at TP1, move your stop toward breakeven once TP1 is tagged, and let the remainder work toward TP2/TP3. The stop-loss level is not a suggestion — respect it as your hard risk boundary regardless of conviction."],
];

const PARAM_COLS = ["Asset Class", "Fast/Slow EMA", "Quant Force Length", "Confirmation Closes", "Min Confluence", "Min Quality", "MTF Checks"];
const PARAM_ROWS = [
  ["Crypto majors (BTC, ETH perps)", "21 / 50", "34", "2", "5/6", "65", "1H / 4H / D"],
  ["Crypto alts (higher volatility)", "13 / 34", "21", "2–3", "5/6", "70", "1H / 4H / D"],
  ["Indices / Futures (ES, NQ, US30)", "21 / 50", "34", "1–2", "4/6", "60", "15m / 1H / 4H"],
  ["Forex majors", "21 / 55", "40", "2", "5/6", "65", "4H / D / W"],
];

const FAQS = [
  { q: "Does the TradrQuant Pro indicator repaint?", a: "No. Every check — trend, momentum, confluence, quality and multi-timeframe — runs exclusively on confirmed candle closes, so a printed setup never moves or disappears afterwards. What you see historically is what you would have seen live." },
  { q: "Is the TradrQuant Pro indicator free?", a: "Yes. TradrQuant Pro is a free confluence terminal on TradingView. It runs a meaningful slice of the same core logic our research process leans on, and every input is exposed for you to study and tune." },
  { q: "What is the difference between the Confluence Score and the Quality Score?", a: "It's a dual-gate structure, not a single blended number. The Confluence Score (0–6) measures how many structural conditions agree on direction; the Quality Score (0–100) is a fully independent gate measuring how clean the move is — momentum strength, volume ratio, candle body-to-range and baseline efficiency. A setup must clear both." },
  { q: "What markets and timeframes does it work on?", a: "It is market-agnostic and works on any TradingView symbol and timeframe — crypto, indices/futures or forex. The starting parameters differ by asset class because volatility profiles differ; treat every value as a forward-testing starting point, not a proven optimization." },
  { q: "How should I trade a printed BUY or SELL setup?", a: "As a starting point for your own trade construction, not an instruction to execute blindly. Read the dashboard (CONF/QUAL/GATE/MTF), grade the setup against nearby zones and Session VWAP, respect the ATR-derived stop as a hard risk boundary, and manage the TP1/TP2/TP3 suite with discipline." },
  { q: "Is this financial advice?", a: "No. The indicator is an educational and analytical tool. It identifies technical confluence — it does not constitute financial advice. Past patterns, however consistent, do not guarantee future results. Always manage your own risk." },
];

const INPUTS = [
  ["Fast / Slow EMA", "Integer", "Lengths of the Trend Cloud ribbon (default 21 / 50). Faster pairs react quicker on noisier instruments."],
  ["Major Trend EMA", "Integer", "The 200-EMA directional filter — your top-level bias layer."],
  ["Quant Force Length", "Integer", "Lookback for the adaptive Kaufman's Efficiency Ratio baseline. Longer = smoother regime read."],
  ["ATR Envelope", "Multiplier", "Band width around the Quant Force baseline used to confirm momentum flips."],
  ["Confirmation Closes", "Integer", "Consecutive closes beyond the band required before a Quant Force flip confirms — filters single-candle noise."],
  ["Min Confluence Score", "0–6", "Minimum of the six confluence checks a setup must satisfy before it is eligible to print."],
  ["Min Quality Score", "0–100", "Minimum quality reading required — the independent second gate."],
  ["Multi-Timeframe Checks", "3 × TF", "The three higher timeframes cross-checked for trend agreement; require for a setup to fire or display read-only."],
  ["Anti-Cluster Cooldown", "Bars", "Minimum bar spacing enforced between setups so chop can't spam repeated triggers."],
  ["Zones & Session VWAP", "Toggles", "Supply/Demand + Support/Resistance context and the anchored intraday VWAP bias filter."],
  ["TP/SL Suite", "ATR-derived", "Full SL, TP1, TP2 and TP3 levels drawn from live ATR rather than fixed points."],
  ["Alerts", "alertcondition", "Native TradingView alert hooks for setups so you can route notifications to app, email or webhook."],
];

const schema = [
  {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "TradrQuant Pro — Confluence Terminal | Documentation, Engine & Parameters",
    description:
      "Complete documentation for the free TradrQuant Pro TradingView indicator: a rule-based trend, momentum and structure engine — Trend Cloud, Quant Force baseline, dual-gate Confluence (0–6) and Quality (0–100) scoring, multi-timeframe confluence, anti-cluster cooldown, how to trade the setups, and starting parameters by asset class.",
    inLanguage: "en",
    image: IMAGE,
    mainEntityOfPage: { "@type": "WebPage", "@id": PAGE_URL },
    author: { "@type": "Person", "@id": PERSON_ID, name: "JC Rod", alternateName: "Tradernaut", url: `${SITE_URL}/about` },
    publisher: { "@type": "Organization", name: "TradrQuant", logo: { "@type": "ImageObject", url: IMAGE } },
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "TradrQuant Pro",
    applicationCategory: "FinanceApplication",
    operatingSystem: "TradingView (Pine Script)",
    url: PAGE_URL,
    downloadUrl: TV_URL,
    description:
      "A free, non-repainting TradingView confluence terminal: a rule-based trend, momentum and structure engine with a dual-gate Confluence (0–6) and Quality (0–100) model, multi-timeframe context and an ATR-derived TP/SL suite.",
    author: { "@type": "Person", "@id": PERSON_ID, name: "JC Rod", alternateName: "Tradernaut" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    screenshot: [
      { "@type": "ImageObject", url: `${SITE_URL}/indicator/btc.webp`, caption: "BTC 5 Min — Premium (Φ) confluence signal" },
      { "@type": "ImageObject", url: `${SITE_URL}/indicator/mnq.webp`, caption: "NQ 5 Min — Premium (Φ) confluence signal" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE_URL}/tools` },
      { "@type": "ListItem", position: 3, name: "TradrQuant Pro Indicator", item: PAGE_URL },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

const Section = ({ id, icon: Icon, kicker, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
      {Icon && <Icon size={16} className="text-[#BACD1C]" />}
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7D8390]">{kicker}</span>
    </div>
    <h2 className="mt-5 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl leading-tight">{title}</h2>
    <div className="mt-4 space-y-4 font-term text-[14px] text-white/60 leading-relaxed">{children}</div>
  </section>
);

export default function IndicatorDocs() {
  useSeo({
    title: "TradrQuant Pro — Confluence Terminal | Free TradingView Indicator Docs | TradrQuant",
    description:
      "TradrQuant Pro is a free, non-repainting confluence terminal for TradingView — a rule-based trend, momentum & structure engine. Full documentation: the engine, how to trade the setups, and starting parameters by asset class.",
    path: PAGE_PATH,
    type: "article",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="indicator-docs" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <JsonLd data={schema} />

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[900px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link to="/" data-testid="docs-back-btn" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300">
            <ChevronLeft size={14} /> cd ~/terminal
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-[900px] px-5 md:px-10 pt-12 md:pt-16 pb-24">
        {/* hero */}
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">
          <LineChart size={14} /> // documentation · tradrquant pro
        </p>
        <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl leading-[0.98]">
          TradrQuant Pro — <span className="text-[#7D8390]">Confluence Terminal</span>
        </h1>
        <p className="mt-6 font-term text-[15px] text-white/70 leading-relaxed max-w-2xl">
          A rule-based trend, momentum, and structure engine built for traders who want to see <em className="text-white/90 not-italic font-semibold">why</em> a setup
          qualifies — not just a colored arrow.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a href={TV_URL} target="_blank" rel="noopener noreferrer" data-testid="docs-tradingview-cta"
            className="inline-flex items-center gap-2 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.14em] px-5 py-3 hover:bg-white transition-colors duration-300">
            Add on TradingView — free <ArrowUpRight size={15} />
          </a>
          <Link to="/research" className="inline-flex items-center gap-2 border border-white/15 font-mono text-xs uppercase tracking-[0.14em] px-5 py-3 text-white/70 hover:text-[#BACD1C] hover:border-[#BACD1C]/40 transition-colors duration-300">
            Read the research →
          </Link>
        </div>

        {/* at a glance */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[["Cost", "Free"], ["Platform", "TradingView"], ["Repaints", "No"], ["Gates", "Dual"]].map(([k, v]) => (
            <div key={k} className="bg-[#0B0B0B] p-4">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/35">{k}</div>
              <div className="mt-1 font-display font-semibold tracking-[-0.03em] text-lg text-[#ECEBE4]">{v}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 space-y-14">
          <Section id="charts" icon={LineChart} kicker="on the chart" title="See it on the chart">
            <p>Premium (Φ) confluence signals as they print live — both gates in agreement on the closed bar. Illustrative examples for education; not a performance claim.</p>
            <div className="not-prose grid md:grid-cols-2 gap-5">
              {[
                { src: "/indicator/btc.webp", label: "BTC · 5 Min", tier: "Premium (Φ) confluence", alt: "TradrQuant Pro indicator on a BTC 5-minute chart showing a Premium confluence signal" },
                { src: "/indicator/mnq.webp", label: "NQ · 5 Min", tier: "Premium (Φ) confluence", alt: "TradrQuant Pro indicator on an NQ 5-minute chart showing a Premium confluence signal" },
              ].map((c) => (
                <figure key={c.src} data-testid={`chart-${c.label.split(" ")[0].toLowerCase()}`} className="border border-white/10 bg-[#13151A] overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-[#0D0E11]">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{c.label}</span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#B4BAC4]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#BACD1C]" /> {c.tier}
                    </span>
                  </div>
                  <img src={c.src} alt={c.alt} loading="lazy" className="w-full h-auto object-contain" />
                </figure>
              ))}
            </div>
          </Section>

          <Section id="what-this-is" icon={LineChart} kicker="what this is" title="What this is">
            <p>
              TradrQuant Pro is a free confluence terminal built on the same core logic our internal research desk uses to
              construct trade ideas: layered trend confirmation, adaptive momentum, independent quality filtering, and
              multi-timeframe context — all visible, all on confirmed candle closes, nothing hidden behind a proprietary
              "AI score."
            </p>
            <p>
              This isn't a plug-and-play black box. It's a research tool. Load it, study how the components interact, and
              tune it to the instrument and timeframe you actually trade. The parameters are exposed because they're meant
              to be used.
            </p>
          </Section>

          <Section id="engine" icon={GitBranch} kicker="the engine" title="The engine">
            <div className="not-prose border border-white/10 divide-y divide-white/10">
              {ENGINE.map(([name, desc], i) => (
                <div key={name} className="flex items-start gap-4 p-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-white/[0.10] text-[#BACD1C] font-mono text-[13px]">{i + 1}</span>
                  <div>
                    <div className="font-display font-semibold tracking-[-0.03em] text-[15px]">{name}</div>
                    <p className="mt-1.5 font-term text-[13px] text-white/60 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p>
              A trade setup only prints when trend, momentum, quality, and — if enabled — multi-timeframe context all
              align. Every check runs exclusively on confirmed candle closes. <strong className="text-white/90">Nothing repaints.</strong>
            </p>
          </Section>

          <Section id="how-to-trade" icon={Layers} kicker="playbook" title="How to actually trade the setups">
            <p>This section matters more than any single parameter. The dashboard and the chart are built to be read together, not in isolation.</p>
            <div className="not-prose space-y-4">
              {TRADE.map(([head, body]) => (
                <div key={head} className="border-l-2 border-white/[0.10] pl-4">
                  <div className="font-display font-semibold tracking-[-0.03em] text-[14px] text-white/90">{head}</div>
                  <p className="mt-1.5 font-term text-[13px] text-white/60 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="parameters" icon={Gauge} kicker="starting points" title="Starting parameters by asset class">
            <p>There is no universal parameter set that outperforms everywhere — treat every value below as a forward-testing starting point tuned to the volatility profile of that asset class, not a proven optimization.</p>
            <div className="not-prose overflow-x-auto border border-white/10">
              <table className="w-full text-left border-collapse min-w-[640px]">
                <thead>
                  <tr className="bg-[#0D0E11]">
                    {PARAM_COLS.map((c) => (
                      <th key={c} className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/40 px-4 py-3 border-b border-white/10 whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PARAM_ROWS.map((row) => (
                    <tr key={row[0]} className="border-b border-white/[0.06] align-top">
                      {row.map((cell, i) => (
                        <td key={i} className={i === 0
                          ? "px-4 py-3 font-display font-semibold tracking-[-0.03em] text-[13px] text-[#ECEBE4]"
                          : "px-4 py-3 font-mono text-[12px] text-white/60 tabular-nums whitespace-nowrap"}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>
              <strong className="text-white/90">Why these differ:</strong> alts and lower-cap crypto move sharper and noisier, so a faster EMA pair
              paired with a higher quality bar filters false starts more aggressively. Indices trend more smoothly intraday,
              so a shorter MTF stack (15m/1H/4H) stays relevant to how index traders actually operate. Forex moves in slower,
              cleaner sessions, so a longer Quant Force length and a higher-timeframe MTF stack (4H/D/W) fits its rhythm
              better than crypto's faster cadence.
            </p>
          </Section>

          <Section id="inputs" icon={LineChart} kicker="reference" title="Input parameters">
            <p>Every input is exposed for a reason. Here is what each one does and how it affects signal frequency and quality. Defaults may change between script versions.</p>
            <div className="not-prose overflow-x-auto border border-white/10">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#0D0E11]">
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 px-4 py-3 border-b border-white/10">Input</th>
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 px-4 py-3 border-b border-white/10">Type</th>
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 px-4 py-3 border-b border-white/10">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {INPUTS.map(([n, t, d]) => (
                    <tr key={n} className="border-b border-white/[0.06] align-top">
                      <td className="px-4 py-3 font-display font-semibold tracking-[-0.03em] text-[13px] text-[#ECEBE4] whitespace-nowrap">{n}</td>
                      <td className="px-4 py-3 font-mono text-[12px] text-white/45 whitespace-nowrap">{t}</td>
                      <td className="px-4 py-3 font-term text-[13px] text-white/60 leading-relaxed">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="alerts" icon={Bell} kicker="automation" title="Alerts & how to add it">
            <p>Setups expose native TradingView <code className="text-[#BACD1C] bg-white/[0.06] px-1">alertcondition</code> hooks, so you can push them to the TradingView app, email or a webhook.</p>
            <ol className="not-prose space-y-2">
              {[
                "Open the TradrQuant Pro script on TradingView and click \u201cAdd to favourites\u201d.",
                "On your chart, open the Indicators panel, go to Favourites and add TradrQuant Pro.",
                "Open Settings to configure the ribbon, Quant Force length, confirmation closes, confluence & quality thresholds and multi-timeframe checks.",
                "Click the Alert (clock) icon, choose TradrQuant Pro as the condition and select the setup you want.",
                "Choose your notification method (app / email / webhook) and create the alert.",
              ].map((s, i) => (
                <li key={i} className="flex gap-3 font-term text-[13px] text-white/60">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center border border-white/[0.10] text-[#BACD1C] font-mono text-[10px]">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
          </Section>

          <Section id="take-advantage" icon={Rocket} kicker="get it" title="Take advantage of it">
            <p>
              This is free, it runs a meaningful slice of the same core logic our research process leans on, and every
              input is exposed for a reason — go load it on your own charts, across your own instruments, and actually tune
              it. The defaults are a starting point, not a finish line.
            </p>
          </Section>

          <Section id="faq" icon={Check} kicker="faq" title="Frequently asked questions">
            <div className="not-prose divide-y divide-white/10 border-y border-white/10">
              {FAQS.map((f) => (
                <div key={f.q} className="py-4">
                  <h3 className="font-display font-semibold tracking-[-0.03em] text-[15px]">{f.q}</h3>
                  <p className="mt-2 font-term text-[13px] text-white/55 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* CTA */}
          <div className="border border-white/[0.10] bg-[#13151A] p-8 text-center">
            <h2 className="font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl">Get the free indicator</h2>
            <p className="mt-3 font-term text-sm text-white/55 max-w-lg mx-auto">Add TradrQuant Pro to your TradingView charts in one click — no cost, no account tier required.</p>
            <a href={TV_URL} target="_blank" rel="noopener noreferrer" data-testid="docs-tradingview-cta-bottom"
              className="mt-6 inline-flex items-center gap-2 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.14em] px-6 py-3.5 hover:bg-white transition-colors duration-300">
              Add on TradingView <ArrowUpRight size={15} />
            </a>
          </div>

          <p className="font-mono text-[11px] text-white/25 leading-relaxed">
            This indicator is an educational and analytical tool. It does not constitute financial advice. Past patterns,
            however consistent, do not guarantee future results. Always manage your own risk.
          </p>
        </div>
      </article>
      <IndicatorReviews />
      <Footer />
    </div>
  );
}
